import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  getEquipmentDetails,
  deactivateEquipment,
  activateEquipment,
  deleteEquipment,
  getEquipmentDeactivationDetails,
  getEquipmentDeletionDetails,
  getGuideBoatsListing,
  updateBoat
} from "store/boats"
import { checkTripBookingConflicts } from "store/crew-profiles"
import { routes } from "router/routes"
import { toaster } from "components/common/Toast"
import { isMediaFileVideo } from "utils/media-upload"
import { useEquipmentModals } from "../../hooks/useEquipmentModals"
import { determineDeactivateMode } from "../../utils/determineDeactivateMode"

/**
 * Custom hook for managing all modal states and handlers in EquipmentDetails
 * @param {string} id - Equipment ID from URL params
 * @param {object} equipment - Mapped equipment data
 * @param {object} deactivationData - Deactivation details and loading states
 * @param {Function} t - Translation function
 * @returns {object} Modal states and handlers
 */
export const useEquipmentDetailsModals = (id, equipment, deactivationData, t) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Destructure deactivation data
  const {
    equipmentDeactivationDetails,
    equipmentDeletionDetails,
    deactivationDetailsLoading,
    deletionDetailsLoading,
    deactivateLoading,
    deleteLoading
  } = deactivationData

  // Use existing hook for deactivate/delete modal state
  const { deactivateDeleteModalState, openDeactivateModal, openDeleteModal, closeDeactivateDeleteModal, setDeactivateDeleteDetails } =
    useEquipmentModals()

  // Get conflict check data from Redux
  const { data: conflictsData, loading: checkingConflicts } = useSelector((state) => state.crewProfiles.tripBookingConflicts)

  // Remove experience conflict modal state
  const [removeConflictModalOpen, setRemoveConflictModalOpen] = useState(false)
  const [removeConflictBookings, setRemoveConflictBookings] = useState([])

  // Assign experiences conflict modal state
  const [assignConflictModalOpen, setAssignConflictModalOpen] = useState(false)
  const [assignConflictExperiences, setAssignConflictExperiences] = useState([])

  // Video player state
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false)
  const [selectedMedia, setSelectedMedia] = useState(null)

  // Assign experiences modal state
  const [showAssignExperiencesModal, setShowAssignExperiencesModal] = useState(false)

  // Remove experience confirmation state
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [experienceToRemove, setExperienceToRemove] = useState(null)
  const [experienceToRemoveName, setExperienceToRemoveName] = useState("")
  const [isCheckingForRemoval, setIsCheckingForRemoval] = useState(false)

  /**
   * Handle conflict check results
   * When API returns, decide whether to show booking conflicts modal or confirmation modal
   */
  useEffect(() => {
    // Only process if we initiated a check for removal and the API call has completed
    if (!checkingConflicts && conflictsData && experienceToRemove && isCheckingForRemoval) {
      const conflictingBookings = conflictsData.conflicting_bookings || []

      if (conflictingBookings.length > 0) {
        // Transform bookings to match RemoveExperienceWithBookingsModal format
        const formattedBookings = conflictingBookings.map((booking) => ({
          id: booking.booking_id,
          experienceName: booking.experience_name || experienceToRemoveName,
          time: `${booking.start_time} - ${booking.end_time}`,
          date: booking.booked_date || "",
          imageUrl: booking.preview || "",
          location: booking.address || "",
          booking_uuid: booking.booking_uuid
        }))

        setRemoveConflictBookings(formattedBookings)
        setRemoveConflictModalOpen(true)
      } else {
        // No conflicts, show confirmation modal
        setShowRemoveConfirmModal(true)
      }

      // Reset the flag after processing
      setIsCheckingForRemoval(false)
    }
  }, [checkingConflicts, conflictsData, experienceToRemove, experienceToRemoveName, isCheckingForRemoval])

  /**
   * Fetch equipment deactivation details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeactivationDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "deactivate") {
      const mode = equipmentDeactivationDetails.mode || determineDeactivateMode(equipmentDeactivationDetails)
      setDeactivateDeleteDetails({
        ...equipmentDeactivationDetails,
        mode
      })
    }
  }, [equipmentDeactivationDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  /**
   * Fetch equipment deletion details when modal is opened
   * Sets the details in modal state once loaded
   */
  useEffect(() => {
    if (equipmentDeletionDetails && deactivateDeleteModalState.visible && deactivateDeleteModalState.action === "delete") {
      const mode = equipmentDeletionDetails.mode || determineDeactivateMode(equipmentDeletionDetails)
      setDeactivateDeleteDetails({
        ...equipmentDeletionDetails,
        mode
      })
    }
  }, [equipmentDeletionDetails, deactivateDeleteModalState.visible, deactivateDeleteModalState.action, setDeactivateDeleteDetails])

  // ============ Equipment Route Handlers ============

  /**
   * Handle edit button click
   * Navigates to the edit equipment route
   */
  const handleEdit = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    navigate(routes.guideEquipmentEditPath({ id }))
  }

  /**
   * Handle duplicate button click
   * Navigates to the duplicate equipment route
   */
  const handleDuplicate = () => {
    if (!id) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }
    navigate(routes.guideEquipmentDuplicatePath({ id }))
  }

  // ============ Video Player Handlers ============

  /**
   * Handle media click in gallery
   * Opens video player modal if media is a video
   */
  const handleMediaClick = (mediaItem) => {
    if (isMediaFileVideo(mediaItem)) {
      setSelectedMedia(mediaItem)
      setVideoPlayerOpen(true)
    }
  }

  /**
   * Handle closing video player modal
   */
  const handleCloseVideoPlayer = () => {
    setVideoPlayerOpen(false)
    setSelectedMedia(null)
  }

  // ============ Assign Experiences Handlers ============

  /**
   * Handle "Assign Experiences" button click
   */
  const handleAssignExperiences = () => {
    setShowAssignExperiencesModal(true)
  }

  /**
   * Handle assign experiences modal close
   */
  const handleAssignExperiencesModalClose = () => {
    setShowAssignExperiencesModal(false)
  }

  /**
   * Handle saving assigned experiences
   */
  const handleAssignExperiencesSave = async (selectedExperienceIds) => {
    try {
      const formData = new FormData()
      for (const tripId of selectedExperienceIds) {
        formData.append("boat[trip_ids][]", tripId)
      }
      if (selectedExperienceIds.length === 0) {
        formData.append("boat[trip_ids][]", "")
      }
      await dispatch(updateBoat(id, formData))
      dispatch(getEquipmentDetails(id))
    } catch (error) {
      // Check for booking conflicts
      if (error?.data?.conflicting_bookings?.length > 0) {
        // Group bookings by experience name
        const bookingsByExperience = {}

        error.data.conflicting_bookings.forEach((booking) => {
          const expName = booking.experience_name || "Unknown Experience"

          if (!bookingsByExperience[expName]) {
            bookingsByExperience[expName] = []
          }

          bookingsByExperience[expName].push({
            id: booking.booking_id,
            experienceName: expName,
            time: `${booking.start_time} - ${booking.end_time}`,
            date: booking.booked_date || "",
            imageUrl: booking.preview || "",
            location: booking.address || "",
            booking_uuid: booking.booking_uuid
          })
        })

        // Convert to array format for the modal
        const conflictingExperiences = Object.keys(bookingsByExperience).map((expName) => ({
          experienceName: expName,
          bookings: bookingsByExperience[expName]
        }))

        setAssignConflictExperiences(conflictingExperiences)
        setAssignConflictModalOpen(true)

        // Close the assign modal
        setShowAssignExperiencesModal(false)

        const conflictError = new Error("Booking conflict")
        conflictError.isBookingConflict = true
        throw conflictError
      }
      throw error
    }
  }

  /**
   * Handle remove experience button click
   * Checks for booking conflicts before showing any modal
   */
  const handleRemoveExperience = (experienceIdToRemove) => {
    if (equipment?.trips?.length === 1) {
      return
    }

    const experienceToDelete = equipment?.trips?.find((trip) => trip.id === experienceIdToRemove)
    const experienceName = experienceToDelete?.title || "this experience"

    // Reset state and close any open modals before starting new check
    setShowRemoveConfirmModal(false)
    setRemoveConflictModalOpen(false)
    setRemoveConflictBookings([])
    setExperienceToRemove(experienceIdToRemove)
    setExperienceToRemoveName(experienceName)
    setIsCheckingForRemoval(true)

    // Check if this experience has any booking conflicts
    dispatch(checkTripBookingConflicts(experienceIdToRemove, id, "boat"))
  }

  /**
   * Handle confirmation of experience removal
   * Called when user confirms removal in the confirmation modal
   */
  const handleConfirmRemove = async () => {
    if (!experienceToRemove || !id) {
      return
    }

    const remainingTripIds = equipment?.trips?.filter((trip) => trip.id !== experienceToRemove)?.map((trip) => trip.id) || []

    const formData = new FormData()
    for (const tripId of remainingTripIds) {
      formData.append("boat[trip_ids][]", tripId)
    }
    if (remainingTripIds.length === 0) {
      formData.append("boat[trip_ids][]", "")
    }

    try {
      await dispatch(updateBoat(id, formData))
      toaster.success(t("equipment.experience_removed_successfully", { defaultValue: "Experience removed successfully" }))
      await dispatch(getEquipmentDetails(id))
    } catch (error) {
      toaster.error(t("equipment.experience_remove_failed", { defaultValue: "Failed to remove experience" }))
      console.error("Error removing experience:", error)
    }

    // Close modal and clear state
    setShowRemoveConfirmModal(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
    setIsCheckingForRemoval(false)
  }

  /**
   * Handle cancellation of experience removal
   * Closes all modals and resets state
   */
  const handleCancelRemove = () => {
    setShowRemoveConfirmModal(false)
    setRemoveConflictModalOpen(false)
    setExperienceToRemove(null)
    setExperienceToRemoveName("")
    setRemoveConflictBookings([])
    setIsCheckingForRemoval(false)
  }

  /**
   * Handle cancellation of assign experiences conflicts
   * Closes the assign conflict modal
   */
  const handleCancelAssignConflicts = () => {
    setAssignConflictModalOpen(false)
    setAssignConflictExperiences([])
  }

  // ============ Deactivate/Delete Handlers ============

  /**
   * Handle deactivate button click
   */
  const handleDeactivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    openDeactivateModal(equipmentForModal)

    dispatch(getEquipmentDeactivationDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle activate button click
   */
  const handleActivate = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    dispatch(activateEquipment(id))
      .then((response) => {
        const successMessage = response?.message || `${equipment.name} has been activated successfully`
        toaster.success(successMessage)
        dispatch(getEquipmentDetails(id))
        dispatch(getGuideBoatsListing())
      })
      .catch((error) => {
        console.error("Equipment activation failed:", error)
      })
  }

  /**
   * Handle delete button click
   */
  const handleDelete = () => {
    if (!id || !equipment) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const equipmentForModal = {
      key: id,
      id: id,
      title: equipment.name
    }

    openDeleteModal(equipmentForModal)

    dispatch(getEquipmentDeletionDetails(id)).catch((error) => {
      console.error("Failed to fetch equipment deletion details:", error)
      closeDeactivateDeleteModal()
    })
  }

  /**
   * Handle deactivate/delete modal confirmation
   * @param {object} data - Additional data for the request
   */
  const handleDeactivateDeleteOk = async (data = {}) => {
    const { equipment: modalEquipment, action } = deactivateDeleteModalState
    const equipmentId = modalEquipment?.key || modalEquipment?.id

    if (!equipmentId) {
      toaster.error(t("equipment.errors.no_equipment_selected"))
      return
    }

    const actionFn = action === "deactivate" ? deactivateEquipment : deleteEquipment

    dispatch(actionFn(equipmentId, data))
      .then(() => {
        toaster.success(
          action === "deactivate"
            ? t("equipment.success.deactivated", { name: modalEquipment.title })
            : t("equipment.success.deleted", { name: modalEquipment.title })
        )
        closeDeactivateDeleteModal()
        dispatch(getGuideBoatsListing())

        if (action === "delete") {
          navigate(routes.guideResourcesPath())
        } else {
          dispatch(getEquipmentDetails(id))
        }
      })
      .catch((error) => {
        console.error("Equipment operation failed:", error)
      })
  }

  // ============ Navigation Handlers ============

  /**
   * Handle "Add New Booking" button click
   */
  const handleAddNewBooking = () => {
    navigate(routes.guideBookingNewPath(), {
      state: {
        filterExperienceIds: equipment?.trips?.map((trip) => trip.id) || [],
        equipmentId: equipment?.id,
        equipmentName: equipment?.name
      }
    })
  }

  /**
   * Handle dropdown menu item clicks
   */
  const handleMenuClick = ({ key }) => {
    switch (key) {
      case "duplicate":
        handleDuplicate()
        break
      case "deactivate":
        if (equipment?.is_active) {
          handleDeactivate()
        } else {
          handleActivate()
        }
        break
      case "delete":
        handleDelete()
        break
      default:
        break
    }
  }

  return {
    // Equipment
    handleEdit,
    handleDuplicate,

    // Video Player
    videoPlayerOpen,
    selectedMedia,
    handleMediaClick,
    handleCloseVideoPlayer,

    // Assign Experiences
    showAssignExperiencesModal,
    handleAssignExperiences,
    handleAssignExperiencesModalClose,
    handleAssignExperiencesSave,

    // Remove Experience Confirmation
    showRemoveConfirmModal,
    experienceToRemoveName,
    handleRemoveExperience,
    handleConfirmRemove,
    handleCancelRemove,
    checkingConflicts,

    // Assign Experiences Conflict Modal (RemoveExperienceWithBookingsModal - grouped view)
    assignConflictModalProps: {
      open: assignConflictModalOpen,
      onCancel: handleCancelAssignConflicts,
      conflictingExperiences: assignConflictExperiences,
      resourceName: equipment?.name || "",
      titleKey: "equipment.assignExperiences.bookingConflictMultiple.title",
      infoKey: "equipment.assignExperiences.bookingConflictMultiple.info",
      bookingsLabelKey: "equipment.assignExperiences.bookingConflictMultiple.bookingsLabel",
      removeButtonKey: "common.close"
    },

    // Remove Experience with Bookings Modal (single experience view)
    removeExperienceWithBookingsModalProps: {
      open: removeConflictModalOpen,
      onCancel: handleCancelRemove,
      bookings: removeConflictBookings,
      resourceName: equipment?.name || "",
      experienceName: experienceToRemoveName,
      titleKey: "equipment.removeExperience.title",
      infoKey: "equipment.removeExperience.info",
      bookingsLabelKey: "equipment.removeExperience.bookingsLabel",
      removeButtonKey: "equipment.removeExperience.removeButton"
    },

    // Deactivate/Delete
    deactivateDeleteModalState,
    handleDeactivate,
    handleActivate,
    handleDelete,
    handleDeactivateDeleteOk,
    closeDeactivateDeleteModal,

    // Menu
    handleMenuClick,

    // Navigation
    handleAddNewBooking,

    // Loading states (pass through)
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  }
}
